import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import PrebuiltBet from './prebuilt-bet/index.js';
import PrebuiltBetsSkeleton from './skeleton/index.js';
import { memo, useEffect, useRef } from 'react';
import { range } from 'mojito/utils';

const classUtils = MojitoCore.Base.classUtils;
const { Swiper, LoaderSuspense, FlexPane, Text, EventCards, PostponedRenderer } =
    MojitoPresentation.Components;
const { CardLoader } = EventCards;

// eslint-disable-next-line react/display-name
const PrebuiltBets = memo(props => {
    const {
        betItems,
        mojitoTools: { config, stringResolver },
        onPrepareCardsToRender,
        isContentLoaded,
        elementRef,
        class: className,
    } = props;
    const skeleton = <PrebuiltBetsSkeleton config={config.prebuiltBetsSkeleton} />;
    const headerLabel = stringResolver.resolveString('$PREBUILT_BETS.TITLE_HEADER');

    useEffect(() => {
        if (!config.showCarousel) {
            onPrepareCardsToRender(range(betItems.length));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [betItems.length, config.showCarousel]);

    // Will disable postponed rendering if content is initially available as it gives unnecessary layout shift
    const initialContentRef = useRef(isContentLoaded);
    const disablePostponedRendering = !!initialContentRef.current;

    return (
        <FlexPane
            class={classUtils.classNames('ta-PrebuiltBets', className)}
            config={config.container}
            elementRef={elementRef}
        >
            {config.showCarouselTitle && (
                <Text class="ta-PrebuiltBetsCarouselTitle" config={config.carouselTitle}>
                    {headerLabel}
                </Text>
            )}
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!isContentLoaded}
                loader={skeleton}
            >
                <PostponedRenderer
                    name={'PrebuiltBetsCarousel'}
                    disabled={disablePostponedRendering}
                >
                    <CardsContent {...props} />
                </PostponedRenderer>
            </LoaderSuspense>
        </FlexPane>
    );
});

const CardsContent = props => {
    const {
        mojitoTools: { config },
        betItems,
        isContentLoaded,
        onPrepareCardsToRender,
    } = props;

    return config.showCarousel ? (
        <FlexPane class="ta-PrebuiltBetsCarousel" config={config.carouselContainer}>
            <Swiper
                config={config.swiper}
                onPrepareSlidesToRender={onPrepareCardsToRender}
                hideNavigationArrows={betItems.length < config.minBetsForEnablingArrowNav}
            >
                {betItems.map(betInfo => rendererCardSlide(betInfo, config, isContentLoaded))}
            </Swiper>
        </FlexPane>
    ) : (
        <FlexPane class="ta-PrebuiltBetsContainer" config={config.betsContainer}>
            {betItems.map(betInfo => (
                <PrebuiltBet
                    key={betInfo.marketId}
                    config={config.prebuiltBet}
                    eventId={betInfo.eventId}
                    marketId={betInfo.marketId}
                    promotionText={betInfo.promotionText}
                    shouldRequestData={props.shouldRequestData}
                    boostedMarketId={betInfo.boostedMarketId}
                />
            ))}
        </FlexPane>
    );
};

function rendererCardSlide(betInfo, config, isContentLoaded) {
    return function CardSlide(readyToLoad) {
        return (
            <CardLoader key={betInfo.id} readyToLoad={readyToLoad}>
                <PrebuiltBet
                    config={config.prebuiltBet}
                    eventId={betInfo.eventId}
                    marketId={betInfo.marketId}
                    boostedMarketId={betInfo.boostedMarketId}
                    promotionText={betInfo.promotionText}
                    shouldRequestData={isContentLoaded}
                />
            </CardLoader>
        );
    };
}

export default PrebuiltBets;
